/**
 * Copyright 2026 Ziz Academy
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Home: React.FC = () => {
  const { login, isLoading, user } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await login({ username, password });
      navigate('/');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Login failed. Please try again.';
      setError(msg);
    }
  };

  return (
    <div className="home">
      <section className="home-hero">
        <div className="home-hero-content">
          <h1 className="home-title">🎓 Welcome to Zizu Academy Exam Portal</h1>
          <p className="home-subtitle">
            A modern, secure, and user‑friendly platform for conducting online examinations.
            Students can take exams with ease, teachers can create and evaluate seamlessly,
            and administrators get clear oversight — all in one place.
          </p>

          <div className="home-highlights">
            <div className="home-highlight">⚡ Fast & Reliable</div>
            <div className="home-highlight">🛡️ Secure & Fair</div>
            <div className="home-highlight">📈 Insightful Results</div>
          </div>

          <div className="home-cta">
            <Link to="/about" className="btn btn-light">Learn More</Link>
            <Link to="/contact" className="btn btn-outline-light">Contact Us</Link>
          </div>
        </div>

        {/* Right-side login panel */}
        {!user && (
          <aside className="home-login-card" aria-label="Login panel">
            <span className="auth-emoji">🔑</span>
            <h2 className="auth-title" style={{ marginBottom: '0.25rem' }}>Member Login</h2>
            <p className="auth-subtitle" style={{ marginBottom: '1.25rem' }}>Access your dashboard</p>

            {error && <div className="alert alert-error" role="alert">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label" htmlFor="home-username">👤 Username</label>
                <input
                  id="home-username"
                  type="text"
                  className="form-input"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="home-password">🔒 Password</label>
                <input
                  id="home-password"
                  type="password"
                  className="form-input"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled={isLoading}>
                {isLoading ? '⌛ Logging in...' : 'Login'}
              </button>
            </form>

            <p style={{ textAlign: 'center', marginTop: '1rem', color: '#718096' }}>
              New here? <Link to="/register" style={{ color: '#667eea', fontWeight: 'bold' }}>Create an account</Link>
            </p>
          </aside>
        )}
      </section>

      <section className="home-features">
        <div className="feature-card">
          <h3>For Students</h3>
          <p>Take timed exams, track your submissions, and review your results instantly.</p>
        </div>
        <div className="feature-card">
          <h3>For Teachers</h3>
          <p>Create question banks, schedule exams, and evaluate responses efficiently.</p>
        </div>
        <div className="feature-card">
          <h3>For Admins</h3>
          <p>Manage users, monitor activity, and maintain a fair testing environment.</p>
        </div>
      </section>
    </div>
  );
};

export default Home;
